import { PaymentPlanStatus } from '../../Dashboard.types';
import { formatCurrency } from '../../utils/currency';
import { formatDate } from '../../utils/date';

export const PaymentPlanCard = ({
  created,
  funded_at,
  reference,
  sellerName,
  amount,
  status,
}: {
  created: string;
  funded_at?: string | null;
  reference: string;
  sellerName: string;
  amount: number;
  status: PaymentPlanStatus;
}) => {
  return (
    <div className="flex items-center justify-between gap-l p-s">
      <div>
        <div className="text-xs font-reg text-structure-40">
          {formatDate(funded_at ? funded_at : created)}
        </div>
        <div className="text-m font-bold">{reference}</div>
        <div className="text-s font-reg text-structure-40">{sellerName}</div>
      </div>
      <div className="flex flex-col items-end gap-xs">
        <div className="text-right text-xs font-reg text-structure-40">
          Invoiced amount:
        </div>
        <div className="text-m font-bold">{formatCurrency(amount)}</div>
        <div className="flex items-center gap-xs text-s font-reg">
          {STATUS_TO_COMPONENT[status]}
        </div>
      </div>
    </div>
  );
};

const StatusDot = ({ color }: { color: string }) => (
  <span className={`inline-block h-[8px] w-[8px] rounded-2xl ${color}`} />
);

const ActiveStatus = () => (
  <>
    <StatusDot color="bg-success-mid" />
    <span>Active</span>
  </>
);

const OverdueStatus = () => (
  <>
    <StatusDot color="bg-error" />
    <span>Overdue</span>
  </>
);

const CompletedStatus = () => (
  <>
    <StatusDot color="bg-structure-60" />
    <span>Completed</span>
  </>
);

const PendingStatus = () => (
  <>
    <StatusDot color="bg-structure-60" />
    <span>Pending</span>
  </>
);

const ScheduledStatus = () => (
  <>
    <StatusDot color="bg-structure-60" />
    <span>Not started</span>
  </>
);

const CancelledStatus = () => (
  <>
    <StatusDot color="bg-structure-60" />
    <span>Cancelled</span>
  </>
);

const ApprovedStatus = () => (
  <>
    <StatusDot color="bg-success-mid" />
    <span>Approved</span>
  </>
);

const ExpiredStatus = () => (
  <>
    <StatusDot color="bg-structure-60" />
    <span>Expired</span>
  </>
);

const UnknownStatus = () => (
  <>
    <StatusDot color="bg-structure-60" />
    <span>Unknown</span>
  </>
);

const DeclinedStatus = () => (
  <>
    <StatusDot color="bg-error" />
    <span>Declined</span>
  </>
);

const PartialStatus = () => (
  <>
    <StatusDot color="bg-warning" />
    <span>Partial approval</span>
  </>
);

const STATUS_TO_COMPONENT = {
  ongoing: <ActiveStatus />,
  overdue: <OverdueStatus />,
  ended: <CompletedStatus />,
  pending: <PendingStatus />,
  approved: <ApprovedStatus />,
  expired: <ExpiredStatus />,
  declined: <DeclinedStatus />,
  scheduled: <ScheduledStatus />,
  cancelled: <CancelledStatus />,
  unknown: <UnknownStatus />,
  partial: <PartialStatus />,
} as const satisfies Record<PaymentPlanStatus, React.ReactNode>;
